var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState } from 'react';
import { useDareAssignee } from 'root/src/client/logic/project/hooks/useDareAssignee';
import { usePreferredPlatform } from 'root/src/client/logic/dropEvent/hooks/usePreferredPlatform';
import { useUserHasGame } from 'root/src/client/v2/drop/hooks/useUserHasGame';
import { useModal } from 'root/src/client/v2/common/contexts/modal.context';
import { KeySelectionModal } from 'root/src/client/v2/drop/features/KeySelectionModal/components/KeySelectionModal';
import { DarePlatform } from 'root/src/shared/@types/platforms';
import { useCreateProjectDropMutation } from 'root/src/client/v2/drop/hooks/useCreateProjectDropMutation';
import { InitialProductDisplayModal, } from 'root/src/client/v2/drop/features/KeySelectionModal/components/InitialProductDisplayModal';
import { useAppDispatch } from 'root/src/client/logic/helpers/useStore';
import { useDropShortUrl } from 'root/src/client/v2/drop/hooks/useDropShortUrl';
import { useLocalStorage } from 'root/src/client/web/hooks/useLocalStorage';
import { DARE_SUBMISSION_MODAL_MODULE_ID } from 'root/src/shared/descriptions/modules/moduleIds';
import displayModal from 'root/src/client/logic/modal/thunks/displayModal';
import { PreferredPlatformReachedMaxDaresModal, } from 'root/src/client/v2/creator/components/PreferredPlatformReachedMaxDaresModal';
import { sentryClientObjMessage } from 'root/src/client/util/sentry';
import moment from 'moment';
import toast from 'react-hot-toast';
import { isNil } from 'remeda';
import { useUser } from 'root/src/client/logic/auth/hooks/useUser';
import { useIsAuthenticated } from 'root/src/client/v2/auth/hooks/useIsAuthenticated';
import { useMaxDaresByPlatform } from 'root/src/client/logic/dropEvent/hooks/useMaxDaresByPlatform';
import { useIsAdmin } from 'root/src/client/v2/auth/hooks/useIsAdmin';
import { useIsBrand } from 'root/src/client/v2/auth/hooks/useIsBrand';
import { isDropComingSoon } from 'root/src/shared/drops/helpers/isDropComingSoon.helper';
import { redirectToCreateBrandProjectForm, } from 'root/src/client/logic/dropEvent/thunks/redirectToCreateBrandProjectForm';
import { useCreatorHasReservedProduct } from 'root/src/client/v2/drop/hooks/useCreatorHasReservedProduct';
export const useAcceptDare = ({ drop, dare }) => {
    var _a, _b;
    const [showSubmissionModal] = useLocalStorage('showSubmissionModal', true);
    const [showPlatformAdjustedModal] = useLocalStorage('showPlatformAdjustedModal', true);
    const [submitting, setSubmitting] = useState(false);
    const modal = useModal();
    const { isAdmin } = useIsAdmin();
    const { isBrand } = useIsBrand();
    const isAdminOrBrand = isAdmin || isBrand;
    const assignee = useDareAssignee({ dare, fallbackDrop: drop });
    const preferredPlatform = usePreferredPlatform();
    const isAuthenticated = useIsAuthenticated();
    const brandId = (_b = (_a = drop === null || drop === void 0 ? void 0 : drop.brand) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : drop === null || drop === void 0 ? void 0 : drop.brandId;
    const userHasGameQuery = useUserHasGame({ dropId: drop.id, brandId }, false);
    const creatorHasReservedProductQuery = useCreatorHasReservedProduct(drop.id, false);
    const shortUrlQuery = useDropShortUrl({ dropId: drop.id, enabled: false });
    const createProjectDropMutation = useCreateProjectDropMutation();
    const creatorCanAccept = useCreatorCanAccept(drop);
    const initialProduct = (drop === null || drop === void 0 ? void 0 : drop.initialProduct) || [];
    const displayPreferredPlatformReachedMaxDaresModal = usePreferredPlatformReachedMaxDaresModal();
    const displaySubmissionPlatformModal = useSubmissionPlatformModal();
    const redirectToCreatProjectForm = useRedirectToCreateProject();
    const user = useUser();
    const comingSoon = isDropComingSoon(drop);
    const onAcceptDare = ({ setHover = defaultSetHover, onSuccess } = {}) => __awaiter(void 0, void 0, void 0, function* () {
        var _c, _d;
        if (!isAuthenticated) {
            return;
        }
        if (isAdminOrBrand) {
            redirectToCreatProjectForm({
                id: dare.id,
                title: dare.title,
                description: dare.description,
                paymentInfo: dare.paymentInfo,
                campaign: { name: drop.campaignName, campaignId: drop.id },
            });
            return;
        }
        if (!creatorCanAccept || !assignee || comingSoon) {
            return;
        }
        if (submitting || dare.userCreated) {
            return;
        }
        const isPreferredPlatformReachedMaxDaresModalVisible = dare.platform === DarePlatform.Any
            && showPlatformAdjustedModal
            && preferredPlatform
            && preferredPlatform !== assignee.platform;
        const isPlatformSubmissionModalVisible = dare.platform !== DarePlatform.Any
            && showSubmissionModal;
        if (isPreferredPlatformReachedMaxDaresModalVisible) {
            const isAccepted = yield displayPreferredPlatformReachedMaxDaresModal(assignee.platform);
            if (!isAccepted) {
                return;
            }
        }
        if (isPlatformSubmissionModalVisible) {
            const isAccepted = yield displaySubmissionPlatformModal();
            if (!isAccepted) {
                return;
            }
        }
        const createProjectPayload = {
            dropDareId: dare.id,
            paymentInfo: dare.paymentInfo,
            assignees: [assignee],
            dropEvent: drop,
            userHasGame: false,
        };
        const { data: userHasGame } = yield userHasGameQuery.refetch();
        const { data: creatorHasReservedProduct } = yield creatorHasReservedProductQuery.refetch();
        if (!userHasGame && !creatorHasReservedProduct) {
            if (initialProduct.length) {
                modal.show(<KeySelectionModal dropEvent={drop} payload={createProjectPayload} dare={dare}/>);
                return;
            }
        }
        const showSuccessModal = () => __awaiter(void 0, void 0, void 0, function* () {
            if (creatorHasReservedProduct || (userHasGame && (drop.isGameFree || !initialProduct))) {
                const shortUrl = yield shortUrlQuery.refetch();
                return modal.show(<InitialProductDisplayModal dropEvent={drop} dare={dare} shortUrl={shortUrl.data}/>, {
                    isCloseable: false,
                });
            }
            return null;
        });
        setSubmitting(true);
        setHover(true);
        try {
            yield createProjectDropMutation.mutateAsync(Object.assign(Object.assign({}, createProjectPayload), { showSuccessModal, onSuccess }));
            setSubmitting(false);
            setHover(false);
        }
        catch (error) {
            console.error(error);
            if (isNil((_d = (_c = user.userData) === null || _c === void 0 ? void 0 : _c.creatorData) === null || _d === void 0 ? void 0 : _d.ccv)) {
                sentryClientObjMessage(`User can't accept dare from "${drop.mnemonicId}" - ${moment.utc().format()}`, Object.assign(Object.assign({ dropId: drop.id, userId: user.authentication.userId, isAuthenticated: true, platformId: assignee.id, platform: assignee.platform, mnemonicId: 0 }, user === null || user === void 0 ? void 0 : user.userData), { error }));
                toast.error('Your data hasn\'t loaded yet. Please reload and try again.');
            }
            else {
                toast.error(error.error);
            }
        }
        setSubmitting(false);
        setHover(false);
    });
    return { onAcceptDare };
};
const usePreferredPlatformReachedMaxDaresModal = () => {
    const modal = useModal();
    return (targetPlatform) => new Promise((resolve) => {
        return modal.show(<PreferredPlatformReachedMaxDaresModal platform={targetPlatform} onClose={() => {
                modal.hide();
                resolve(false);
            }} onSubmit={() => {
                modal.hide();
                resolve(true);
            }}/>, {
            isCloseable: false,
        });
    });
};
const useSubmissionPlatformModal = () => {
    const dispatch = useAppDispatch();
    return () => new Promise((resolve) => {
        dispatch(displayModal(DARE_SUBMISSION_MODAL_MODULE_ID, {
            onCancel: () => {
                displayModal(undefined);
                resolve(false);
            },
            onAccept: () => __awaiter(void 0, void 0, void 0, function* () {
                // !!!! This displayModal(undefined) is important.
                // !!!! Otherwise, we're not closing modal properly, leading to creating duplicate dares.
                displayModal(undefined);
                resolve(true);
            }),
            onClose: () => {
                displayModal(undefined);
                resolve(false);
            },
        }));
    });
};
const useCreatorCanAccept = (drop) => {
    const maxDares = useMaxDaresByPlatform({ fallbackDrop: drop });
    const { isBrand } = useIsBrand();
    const { isAdmin } = useIsAdmin();
    return !isAdmin && !isBrand && maxDares > 0;
};
const defaultSetHover = () => null;
const useRedirectToCreateProject = () => {
    const dispatch = useAppDispatch();
    return (params) => {
        dispatch(redirectToCreateBrandProjectForm(params));
    };
};
